/**
 * Pure helpers for the Avalara company sync.
 *
 * The sync state lives in memory on each backend pod, so two status reads can come from
 * two pods. The page therefore tracks one run from its start signal until it sees that
 * run end, or until nothing confirms it for COMPANY_SYNC_RESULT_TIMEOUT_MS.
 */
import type {
  AvalaraCompanySyncSource,
  AvalaraCompanySyncStatus,
} from '@/types/avalaraSync'
import { COMPANY_SYNC_ERROR_CODES } from '@/types/avalaraSync'
import { formatDateWithTimeLocal } from '@/util/dateUtils'
import { formatCompanySyncToast } from '@/util/avalaraSyncToast'
import type { SyncToastContent } from '@/util/avalaraSyncToast'

/** About 3.5 times a prod run (85 s). Counted from the last sign that the run lives. */
export const COMPANY_SYNC_RESULT_TIMEOUT_MS = 300_000

/**
 * The browser clock sets the anchor of our own run and the server clock sets StartedAt.
 * A browser clock up to this far ahead still matches its own run.
 */
export const CLOCK_SKEW_TOLERANCE_MS = 60_000

export interface TrackedCompanySync {
  /** Earliest StartedAt (epoch ms) that can belong to the tracked run */
  anchorMs: number
  /** Client time of the last sign that the run lives */
  lastEvidenceMs: number
  startedAt?: string
  requestedBy?: string
  source?: AvalaraCompanySyncSource
}

export type CompanySyncOutcome =
  | { kind: 'completed' | 'failed'; status: AvalaraCompanySyncStatus }
  | { kind: 'unknown' }

/**
 * Parses an ISO-8601 instant to epoch ms, or null. The backend sends 1 to 9 fraction
 * digits. ECMAScript date strings take 3, so a longer fraction is cut first.
 */
export function parseInstant(iso: string | undefined): number | null {
  if (!iso) return null
  const cut = iso.replace(/(\.\d{3})\d+/, '$1')
  const ms = Date.parse(cut)
  return Number.isNaN(ms) ? null : ms
}

/**
 * Applies one status read to the tracked run. `status` is null when the request failed.
 * Returns the new tracked run and, when the run ended or timed out, its outcome.
 */
export function advanceCompanySyncTracking(
  tracked: TrackedCompanySync | null,
  status: AvalaraCompanySyncStatus | null,
  nowMs: number,
): { tracked: TrackedCompanySync | null; outcome: CompanySyncOutcome | null } {
  if (tracked === null) {
    if (status?.status === 'RUNNING') {
      return {
        tracked: {
          anchorMs: parseInstant(status.startedAt) ?? nowMs,
          lastEvidenceMs: nowMs,
          startedAt: status.startedAt,
          requestedBy: status.requestedBy,
          source: status.source,
        },
        outcome: null,
      }
    }
    return { tracked: null, outcome: null }
  }

  if (status?.status === 'RUNNING') {
    return {
      tracked: {
        ...tracked,
        lastEvidenceMs: nowMs,
        startedAt: status.startedAt ?? tracked.startedAt,
        requestedBy: status.requestedBy ?? tracked.requestedBy,
        source: status.source ?? tracked.source,
      },
      outcome: null,
    }
  }

  if (status && (status.status === 'COMPLETED' || status.status === 'FAILED')) {
    const startedMs = parseInstant(status.startedAt)
    if (startedMs !== null && startedMs >= tracked.anchorMs) {
      return {
        tracked: null,
        outcome: {
          kind: status.status === 'COMPLETED' ? 'completed' : 'failed',
          status,
        },
      }
    }
  }

  if (nowMs - tracked.lastEvidenceMs >= COMPANY_SYNC_RESULT_TIMEOUT_MS) {
    return { tracked: null, outcome: { kind: 'unknown' } }
  }
  return { tracked, outcome: null }
}

export function formatCompanySyncCompletion(
  status: AvalaraCompanySyncStatus,
): SyncToastContent {
  if (status.result) return formatCompanySyncToast(status.result)
  return {
    severity: 'success',
    summary: 'Companies synced',
    detail: 'The sync ended. No counts were returned.',
  }
}

export function formatCompanySyncFailure(status: AvalaraCompanySyncStatus): {
  summary: string
  detail: string
} {
  let summary = 'Sync from Avalara failed'
  if (status.error?.code === COMPANY_SYNC_ERROR_CODES.BULK_ALIGN_BUSY) {
    summary = 'Sync stopped by a bulk align'
  } else if (status.error?.code === COMPANY_SYNC_ERROR_CODES.SAFETY_GUARD) {
    summary = 'Sync aborted by safety guard'
  }
  return {
    summary,
    detail:
      status.error?.message || 'The sync failed. No error detail was returned.',
  }
}

export function formatCompanySyncRunningDetail(
  tracked: TrackedCompanySync,
): string {
  const startedMs = parseInstant(tracked.startedAt)
  const time =
    startedMs === null ? '' : formatDateWithTimeLocal(new Date(startedMs))
  if (!time) return 'Sync in progress'
  if (tracked.source === 'SCHEDULED') return `Scheduled sync started ${time}`
  return tracked.requestedBy
    ? `Started ${time} by ${tracked.requestedBy}`
    : `Started ${time}`
}
